import { useEffect, useState } from 'react';
import type { ShelfLifeCategory } from '@chicare/shared';

import {
  useRunShelfLifePass,
  useSaveSettings,
  useSetCategoryShelfLife,
  useShelfLife,
  useShelfLifePreview,
} from '@/lib/adminQueries';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { AdminError } from './AdminError';

/**
 * Shelf life and auto-delisting (§9.3).
 *
 * The whole screen is built around one idea: raising the minimum is a
 * destructive action wearing a number field's clothes. Going from six months to
 * nine can pause hundreds of listings across dozens of pharmacies the moment it
 * saves. So the impact preview updates as the admin drags the number, and the
 * save button says what it is about to do.
 */
export function ShelfLifeScreen() {
  const { data, isLoading, isError, error, refetch } = useShelfLife();
  const saveSettings = useSaveSettings();
  const runPass = useRunShelfLifePass();

  const [months, setMonths] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [debouncedMonths, setDebouncedMonths] = useState<number | null>(null);

  const current = data?.platform.min_shelf_life_months ?? 6;
  const proposed = months ?? current;
  const changed = proposed !== current;

  // Debounced so dragging the slider does not fire a preview per pixel.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedMonths(changed ? proposed : null), 300);

    return () => window.clearTimeout(timer);
  }, [proposed, changed]);

  const preview = useShelfLifePreview(debouncedMonths);

  if (isError) {
    return (
      <AdminError
        title="We couldn't load the shelf-life rules"
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-[1100px] space-y-4 px-4 py-6 xl:px-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-56 w-full" radius="card" />
        <Skeleton className="h-72 w-full" radius="card" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-6">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">
          Shelf life &amp; auto-delisting
        </h1>
        <p className="mt-1 max-w-[70ch] text-[13px] text-muted">
          A batch with less remaining life than the minimum cannot be sold. The number resolves most
          specific first: <b>product → category → this platform default</b>. Listings that breach it
          pause automatically overnight, and come back by themselves once a pharmacy adds a
          longer-dated batch.
        </p>
      </header>

      {/* -- The number, and what changing it would do ---------------------- */}
      <Card className="mb-4">
        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <h2 className="m-0 font-display text-[17px] font-bold tracking-[-0.015em]">
              Platform minimum
            </h2>
            <p className="mt-1 text-xs text-muted">
              Applies wherever a category or a product does not set its own.
            </p>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display text-[44px] font-extrabold leading-none tracking-[-0.03em] tabular">
                {proposed}
              </span>
              <span className="text-sm text-muted">months</span>
              {changed ? (
                <Badge tone="caution">was {current}</Badge>
              ) : (
                <Badge tone="verified">in force</Badge>
              )}
            </div>

            <input
              type="range"
              min={1}
              max={24}
              step={1}
              value={proposed}
              onChange={(event) => setMonths(Number(event.target.value))}
              aria-label="Minimum shelf life in months"
              className="mt-4 w-full accent-[var(--color-blue)]"
            />

            <div className="mt-1 flex justify-between font-mono text-[10px] text-faint">
              <span>1 mo</span>
              <span>12 mo</span>
              <span>24 mo</span>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              {[3, 6, 7, 9, 12].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setMonths(preset)}
                  className={cn(
                    'rounded-pill border px-3 py-1.5 text-[11.5px] font-medium',
                    proposed === preset
                      ? 'border-blue bg-blue text-white'
                      : 'border-line bg-paper text-muted hover:border-blue hover:text-blue',
                  )}
                >
                  {preset} months
                </button>
              ))}
            </div>
          </div>

          <ImpactPanel
            changed={changed}
            loading={preview.isFetching}
            preview={preview.data}
            proposed={proposed}
            current={current}
          />
        </div>

        {changed ? (
          <div className="mt-5 border-t border-line-2 pt-4">
            <label htmlFor="shelf-reason" className="mb-[5px] flex items-center gap-2 text-[11px] font-bold text-muted">
              <Icon name="alert" size={13} className="text-caution" />
              Why are you changing this? <span className="text-danger">required</span>
            </label>
            <textarea
              id="shelf-reason"
              rows={2}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. PCN guidance: nine months minimum on oral solids from Q4."
              className="w-full rounded-inp border border-line bg-white px-3 py-2.5 text-[13px] outline-none placeholder:text-faint focus:border-blue focus:ring-2 focus:ring-blue/15"
            />

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                loading={saveSettings.isPending}
                disabled={reason.trim().length < 3}
                onClick={() =>
                  saveSettings.mutate(
                    { values: { 'catalogue.min_shelf_life_months': proposed }, reason },
                    { onSuccess: () => { setMonths(null); setReason(''); } },
                  )
                }
              >
                {preview.data && preview.data.would_delist > 0
                  ? `Save — pauses ${preview.data.would_delist} listing${preview.data.would_delist === 1 ? '' : 's'}`
                  : 'Save minimum'}
              </Button>
              <Button variant="grey" onClick={() => { setMonths(null); setReason(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      {/* -- The rest of the rules ----------------------------------------- */}
      <div className="mb-4 grid gap-4 md:grid-cols-3">
        <Stat
          label="Warning band"
          value={`${data.platform.expiry_warning_months} months`}
          help="How far above the minimum a batch shows amber. Amber means about to pause."
        />
        <Stat
          label="Grace period"
          value={
            data.platform.delist_grace_days === 0
              ? 'None'
              : `${data.platform.delist_grace_days} days`
          }
          help="Time between breaching and pausing, so a pharmacy mid-restock is not punished."
        />
        <Stat
          label="Auto-delisting"
          value={data.platform.auto_delist_enabled ? 'On' : 'Off'}
          tone={data.platform.auto_delist_enabled ? 'verified' : 'danger'}
          help={
            data.platform.auto_delist_enabled
              ? 'Runs nightly at 04:30 WAT.'
              : 'Short-dated stock stays sellable until someone notices. This should normally be on.'
          }
        />
      </div>

      {/* -- Category overrides -------------------------------------------- */}
      <CategoryOverrides categories={data.categories} platformDefault={current} />

      {/* -- Currently paused ---------------------------------------------- */}
      <Card flush className="mt-4">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-line-2 px-4 py-3.5">
          <h2 className="m-0 font-display text-[15px] font-bold tracking-[-0.015em]">
            Paused for shelf life
          </h2>
          <Badge tone={data.delisted_total > 0 ? 'caution' : 'verified'}>
            {data.delisted_total}
          </Badge>
          <span className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="grey"
              loading={runPass.isPending && runPass.variables?.dryRun === true}
              onClick={() => runPass.mutate({ dryRun: true })}
            >
              Dry run
            </Button>
            <Button
              size="sm"
              loading={runPass.isPending && runPass.variables?.dryRun === false}
              onClick={() => runPass.mutate({ dryRun: false, reason: 'Run manually from the console.' })}
            >
              Run now
            </Button>
          </span>
        </div>

        <DataTable
          columns={delistedColumns}
          rows={data.currently_delisted}
          rowKey={(row) => row.listing_id}
          empty="Nothing is paused. Every live listing has enough shelf life left."
        />
      </Card>

      {runPass.data ? (
        <Card className="mt-4">
          <div className="mb-2 flex items-center gap-2">
            <Icon name="doc" size={15} className="text-muted" />
            <b className="text-[13px]">
              {runPass.data.dry_run ? 'Dry run — nothing was changed' : 'Pass complete'}
            </b>
          </div>
          <pre className="max-h-72 overflow-auto rounded-lg bg-ink p-3.5 font-mono text-[11px] leading-relaxed text-[#C6D3E3]">
            {runPass.data.output.trim()}
          </pre>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * The impact panel. This is the reason the whole screen exists: an admin gets
 * to see what a change will do before they do it, not in the support queue the
 * next morning.
 */
function ImpactPanel({
  changed,
  loading,
  preview,
  proposed,
  current,
}: {
  changed: boolean;
  loading: boolean;
  preview?: import('@chicare/shared').ShelfLifePreview;
  proposed: number;
  current: number;
}) {
  if (!changed) {
    return (
      <div className="rounded-card border border-line bg-paper p-4">
        <p className="m-0 text-xs leading-relaxed text-muted">
          Move the slider to see exactly which listings a new minimum would pause or restore, before
          anything is saved.
        </p>
      </div>
    );
  }

  const raising = proposed > current;

  return (
    <div
      className={cn(
        'rounded-card border p-4',
        raising ? 'border-caution bg-caution-tint/40' : 'border-verified bg-verified-tint/40',
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <Icon
          name={raising ? 'alert' : 'check'}
          size={15}
          className={raising ? 'text-caution' : 'text-verified'}
        />
        <b className={cn('text-[13px]', raising ? 'text-caution-ink' : 'text-verified-ink')}>
          {current} → {proposed} months
        </b>
      </div>

      {loading || !preview ? (
        <div className="space-y-2">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-32" />
        </div>
      ) : (
        <>
          <dl className="m-0 space-y-1.5 text-xs">
            <Row label="Sellable now" value={preview.currently_sellable} />
            <Row
              label="Would pause"
              value={preview.would_delist}
              tone={preview.would_delist > 0 ? 'danger' : undefined}
            />
            <Row
              label="Would come back"
              value={preview.would_restore}
              tone={preview.would_restore > 0 ? 'verified' : undefined}
            />
            <Row label="Pharmacies affected" value={preview.affected_pharmacies} />
          </dl>

          {preview.examples.length > 0 ? (
            <div className="mt-3 border-t border-line-2 pt-3">
              <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-faint">
                For example
              </p>
              <ul className="m-0 list-none space-y-1 p-0">
                {preview.examples.slice(0, 4).map((example) => (
                  <li key={example.listing_id} className="text-[11px] leading-snug text-muted">
                    <b className="text-ink">{example.product}</b> · {example.pharmacy} ·{' '}
                    <span className="font-mono tabular">{example.months_remaining}mo left</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'danger' | 'verified';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd
        className={cn(
          'm-0 font-mono font-bold tabular',
          tone === 'danger' && 'text-danger',
          tone === 'verified' && 'text-verified',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Stat({
  label,
  value,
  help,
  tone,
}: {
  label: string;
  value: string;
  help: string;
  tone?: 'verified' | 'danger';
}) {
  return (
    <Card>
      <div className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-faint">{label}</div>
      <div
        className={cn(
          'mt-1.5 font-display text-[22px] font-extrabold tracking-[-0.025em]',
          tone === 'danger' && 'text-danger',
          tone === 'verified' && 'text-verified',
        )}
      >
        {value}
      </div>
      <p className="mt-1.5 text-[11px] leading-relaxed text-faint">{help}</p>
    </Card>
  );
}

function CategoryOverrides({
  categories,
  platformDefault,
}: {
  categories: ShelfLifeCategory[];
  platformDefault: number;
}) {
  const setCategory = useSetCategoryShelfLife();
  const [editing, setEditing] = useState<number | null>(null);
  const [value, setValue] = useState<string>('');

  return (
    <Card flush>
      <div className="border-b border-line-2 px-4 py-3.5">
        <h2 className="m-0 font-display text-[15px] font-bold tracking-[-0.015em]">
          Per-category overrides
        </h2>
        <p className="mt-1 text-[11px] text-muted">
          Cold-chain insulin and a box of plasters do not want the same number. A category that
          inherits follows the platform default of {platformDefault} months.
        </p>
      </div>

      <ul className="m-0 list-none divide-y divide-line-2 p-0">
        {categories.map((category) => (
          <li
            key={category.id}
            className={cn(
              'flex flex-wrap items-center gap-3 px-4 py-3',
              category.parent_id && 'pl-8',
            )}
          >
            <span className="min-w-0 flex-1 text-[13px]">
              {category.name}
              {category.parent_id ? null : <span className="ml-2 text-[10px] text-faint">top level</span>}
            </span>

            {editing === category.id ? (
              <span className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={36}
                  value={value}
                  placeholder="inherit"
                  onChange={(event) => setValue(event.target.value)}
                  className="h-9 w-24 rounded-inp border border-line px-2.5 font-mono text-xs tabular outline-none focus:border-blue"
                />
                <Button
                  size="sm"
                  loading={setCategory.isPending}
                  onClick={() =>
                    setCategory.mutate(
                      {
                        categoryId: category.id,
                        months: value === '' ? null : Number(value),
                        reason: `Set ${category.name} shelf life from the console.`,
                      },
                      { onSuccess: () => setEditing(null) },
                    )
                  }
                >
                  Save
                </Button>
                <Button size="sm" variant="grey" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </span>
            ) : (
              <span className="flex items-center gap-3">
                {category.inherits ? (
                  <Badge>inherits · {category.effective_months} mo</Badge>
                ) : (
                  <Badge tone="brand">{category.min_shelf_life_months} months</Badge>
                )}
                <Button
                  size="xs"
                  variant="grey"
                  onClick={() => {
                    setEditing(category.id);
                    setValue(category.min_shelf_life_months?.toString() ?? '');
                  }}
                >
                  {category.inherits ? 'Override' : 'Change'}
                </Button>
              </span>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const delistedColumns: Column<{
  listing_id: number;
  product: string | null;
  pharmacy: string | null;
  delisted_at: string | null;
}>[] = [
  { key: 'product', header: 'Product', render: (row) => <b className="text-[12.5px]">{row.product}</b> },
  {
    key: 'pharmacy',
    header: 'Pharmacy',
    render: (row) => <span className="text-[11px] text-muted">{row.pharmacy}</span>,
  },
  {
    key: 'delisted_at',
    header: 'Paused',
    render: (row) => (
      <span className="font-mono text-[11px] text-faint tabular">
        {row.delisted_at ? new Date(row.delisted_at).toLocaleDateString('en-NG') : '—'}
      </span>
    ),
  },
];
